import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import { parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../../../core/CountryCodeSelector/CountryCodeSelector.css';

interface CountryCodeSelectorStandaloneProps {
	countryCode: string;
	mobileNumber: string;
	onCountryCodeChange: (value: string) => void;
	onMobileNumberChange: (value: string) => void;
	error?: string;
}

const CountryCodeSelectorStandalone: React.FC<CountryCodeSelectorStandaloneProps> = ({
	countryCode,
	mobileNumber,
	onCountryCodeChange,
	onMobileNumberChange,
	error,
}) => {
	const { t } = useTranslation();

	// Get country ISO code from country calling code (e.g., "+1" -> "US")
	const getCountryFromCode = (code: string): string | undefined => {
		if (!code) return undefined;
		try {
			// Try to parse with a dummy number to get country
			const dummyNumber = code + '1234567890';
			const phoneNumber = parsePhoneNumber(dummyNumber);
			return phoneNumber?.country || undefined;
		} catch {
			// Fallback: common country codes mapping
			const codeMap: { [key: string]: string } = {
				'+1': 'US',
				'+52': 'MX',
				'+54': 'AR',
				'+55': 'BR',
				'+34': 'ES',
				'+51': 'PE',
				'+56': 'CL',
				'+57': 'CO',
			};
			return codeMap[code] || undefined;
		}
	};

	const phoneInputCountry = useMemo(() => {
		return getCountryFromCode(countryCode) || 'US';
	}, [countryCode]);

	// Internal state for PhoneInput value - only updated when country changes, not when number changes
	const [phoneInputValue, setPhoneInputValue] = useState<string | undefined>(undefined);

	// Sync phoneInputValue only when countryCode changes (not when mobileNumber changes)
	useEffect(() => {
		if (countryCode) {
			// Set only the country code, not the full number
			setPhoneInputValue(countryCode);
		} else {
			setPhoneInputValue(undefined);
		}
	}, [countryCode]);

	const handlePhoneChange = (value: string | undefined) => {
		// This handler is only called when user changes the country selector in PhoneInput
		if (!value) {
			setPhoneInputValue(undefined);
			onCountryCodeChange('');
			return;
		}

		try {
			const phoneNumber = parsePhoneNumber(value);
			if (phoneNumber) {
				const countryCallingCode = phoneNumber.countryCallingCode;
				// Only update the country code, preserve the existing mobile number
				onCountryCodeChange(`+${countryCallingCode}`);
				// Update internal state to reflect the new country code
				setPhoneInputValue(`+${countryCallingCode}`);
			} else {
				// Fallback: try to extract country code manually
				const match = value.match(/^\+(\d{1,3})/);
				if (match) {
					const newCode = `+${match[1]}`;
					onCountryCodeChange(newCode);
					setPhoneInputValue(newCode);
				}
			}
		} catch {
			// Fallback: if parsing fails, try to extract manually
			const match = value.match(/^\+(\d{1,3})/);
			if (match) {
				const newCode = `+${match[1]}`;
				onCountryCodeChange(newCode);
				setPhoneInputValue(newCode);
			}
		}
	};

	return (
		<>
			<div className="row">
				<div className="col-5 col-md-4 col-lg-4 country-code-left">
					<div className="textbox-country-indicator">
						<div className="custom-country-title">{t('countryCodeSelector.title')}</div>
						<div className="country-info">
							<PhoneInput
								autoComplete="off"
								id="phoneInputId-profile"
								name="mobileNumber-profile"
								international
								defaultCountry="US"
								country={phoneInputCountry}
								placeholder="+1"
								value={phoneInputValue as any}
								onChange={handlePhoneChange}
								countries={[
									'AG',
									'AR',
									'AW',
									'BS',
									'BB',
									'BZ',
									'BO',
									'BQ',
									'BR',
									'CA',
									'CL',
									'CO',
									'CR',
									'CU',
									'CW',
									'DM',
									'EC',
									'SV',
									'US',
									'GD',
									'GL',
									'GT',
									'GF',
									'GQ',
									'GY',
									'HT',
									'HN',
									'KY',
									'TC',
									'VG',
									'JM',
									'MQ',
									'MX',
									'NI',
									'PA',
									'PY',
									'PE',
									'PR',
									'DO',
									'KN',
									'VC',
									'LC',
									'SR',
									'TT',
									'UY',
									'VE',
									'AL',
									'DZ',
									'AM',
									'AT',
									'AZ',
									'BY',
									'BE',
									'BA',
									'BG',
									'CN',
									'HR',
									'CY',
									'CZ',
									'DK',
									'EG',
									'EE',
									'FI',
									'FR',
									'GE',
									'DE',
									'GR',
									'HK',
									'HU',
									'IS',
									'IN',
									'IE',
									'IL',
									'IT',
									'JP',
									'JO',
									'XK',
									'LV',
									'LB',
									'LY',
									'LI',
									'LT',
									'LU',
									'MT',
									'MD',
									'ME',
									'MA',
									'NL',
									'NZ',
									'NG',
									'MK',
									'NO',
									'PS',
									'PL',
									'PT',
									'RO',
									'RU',
									'RS',
									'SG',
									'SK',
									'SI',
									'ZA',
									'KR',
									'ES',
									'SE',
									'CH',
									'SY',
									'TW',
									'TN',
									'TR',
									'UA',
									'GB',
								]}
							/>
						</div>
					</div>
				</div>
				<div className="col-7 col-md-8 col-lg-8 country-code-right">
					<div className="input-container label-container">
						<input
							type="tel"
							name="mobileNumber-profile-input"
							className="textbox-tel textbox-general"
							value={mobileNumber}
							onChange={(e) => {
								const newValue = e.target.value;
								onMobileNumberChange(newValue);
							}}
							placeholder={''}
						/>
						<label className="label-input">{t('countryCodeSelector.placeholder')}</label>
					</div>
				</div>
			</div>
			{error && <div className="error-message-validation">{error}</div>}
		</>
	);
};

export default CountryCodeSelectorStandalone;
